'use client';

/* @jsxRuntime classic */

import React, { useContext, useRef } from 'react';
import { mergeProps } from 'react-aria';
import type { PressEvent, PressEvents } from 'react-aria';
import type {
    DialogTriggerProps as AriaDialogTriggerProps,
    MenuTriggerProps as AriaMenuTriggerProps,
} from 'react-aria-components';
import {
    DialogTrigger as AriaDialogTrigger,
    MenuTrigger as AriaMenuTrigger,
    OverlayTriggerStateContext,
    PopoverContext,
    useSlottedContext,
} from 'react-aria-components';

const ROMA_POPOVER_DIALOG_TRIGGER_DISPLAY_NAME = 'RomaPopoverDialogTrigger';
const ROMA_POPOVER_MENU_TRIGGER_DISPLAY_NAME = 'RomaPopoverMenuTrigger';

export type PopoverDialogTriggerProps = AriaDialogTriggerProps;
export type PopoverMenuTriggerProps = AriaMenuTriggerProps;

/**
 * Use `PopoverDialogTrigger` for non-menu popovers such as detail panels, forms, and other anchored overlays
 * that should follow dialog semantics instead of menu semantics.
 */
export const PopoverDialogTrigger: React.FC<PopoverDialogTriggerProps> = ({ children, ...props }) => (
    <AriaDialogTrigger {...props}>{children}</AriaDialogTrigger>
);

PopoverDialogTrigger.displayName = ROMA_POPOVER_DIALOG_TRIGGER_DISPLAY_NAME;

/**
 * Adds completed-press closing to React Aria's mouse-down opening without owning a second open state.
 */
const MenuTriggerPress: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const state = useContext(OverlayTriggerStateContext);
    const popover = useSlottedContext(PopoverContext);
    const pressStartedOpenRef = useRef(false);
    const triggerChildren = React.Children.toArray(children);

    // Controlled cursor-anchored menus provide only popover content and use an external trigger ref.
    if (triggerChildren.length < 2) return React.createElement(React.Fragment, undefined, children);

    const [trigger, ...content] = triggerChildren;

    /**
     * Touch and keyboard already toggle upstream; only the actual trigger may close this menu.
     */
    const isTriggerPress = (event: PressEvent) =>
        event.pointerType !== 'touch' &&
        event.pointerType !== 'keyboard' &&
        Boolean(popover?.triggerRef?.current?.contains(event.target));

    const pressProps: PressEvents & Pick<React.DOMAttributes<Element>, 'onPointerDownCapture'> = {
        // Press-start fires again after pointer re-entry. Capture physical gestures only once, before Aria opens.
        onPointerDownCapture: (event) => {
            if (event.button === 0 && event.currentTarget === popover?.triggerRef?.current) {
                pressStartedOpenRef.current = state?.isOpen ?? false;
            }
        },
        onPressStart: (event) => {
            // Screen-reader activation has no physical pointer-down.
            if (event.pointerType === 'virtual' && isTriggerPress(event)) {
                pressStartedOpenRef.current = state?.isOpen ?? false;
            }
        },
        onPress: (event) => {
            if (!isTriggerPress(event) || !pressStartedOpenRef.current) return;

            pressStartedOpenRef.current = false;
            state?.close();
        },
    };

    if (!React.isValidElement<React.PropsWithChildren<PressEvents>>(trigger)) {
        return (
            <>
                {trigger}
                {content}
            </>
        );
    }

    const triggerWithPress =
        trigger.type === React.Fragment
            ? React.cloneElement(trigger, undefined, <MenuTriggerPress>{trigger.props.children}</MenuTriggerPress>)
            : React.cloneElement(
                  trigger,
                  // React Aria invokes the merged press handlers after render.
                  // oxlint-disable-next-line react/refs
                  mergeProps(pressProps, trigger.props),
              );

    return (
        <>
            {triggerWithPress}
            {content}
        </>
    );
};

/**
 * Use `PopoverMenuTrigger` for menu-style popovers such as menus, context menus, and submenu overlays
 * that need menu keyboard semantics.
 */
export const PopoverMenuTrigger: React.FC<PopoverMenuTriggerProps> = ({ children, ...props }) => (
    <AriaMenuTrigger {...props}>
        {!props.trigger || props.trigger === 'press' ? <MenuTriggerPress>{children}</MenuTriggerPress> : children}
    </AriaMenuTrigger>
);

PopoverMenuTrigger.displayName = ROMA_POPOVER_MENU_TRIGGER_DISPLAY_NAME;
